"use client";

import { colors } from "@softmax/design-system/tokens.stylex";

import type {
  WikiRevision,
  WikiLoadState,
  WikiRefreshHandler,
  WikiLinkRenderer,
} from "../wiki-types";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../wiki.styles";
import { WikiResourceStatus } from "../wiki-resource-status";
import { WikiRevisionRow } from "./wiki-revision-row";
import { WikiRevisionDiff } from "./wiki-revision-diff";

export function WikiHistory({
  history,
  currentRevisionId,
  canEdit,
  revertingId,
  onRevert,
  onRefresh,
  refreshable = true,
  renderLink,
  diff,
  onDiff,
}: Readonly<{
  history: WikiLoadState<readonly WikiRevision[]>;
  revertingId: string | null;
  onRevert(revisionId: string): void;
  onRefresh: WikiRefreshHandler;
  refreshable?: boolean;
  currentRevisionId: string | undefined;
  canEdit: boolean;
  renderLink: WikiLinkRenderer;
  diff: Readonly<{ older: WikiRevision; newer: WikiRevision }> | null;
  onDiff(value: Readonly<{ older: WikiRevision; newer: WikiRevision }>): void;
}>) {
  const revisions = history.status === "ready" ? history.data : [];
  return (
    <section {...stylex.props(styles.section)}>
      <h2 {...stylex.props(styles.sectionTitle)}>Revision history</h2>
      {history.status === "ready" && (
        <p {...stylex.props(styles.smallMuted)}>
          {revisions.length} {revisions.length === 1 ? "revision" : "revisions"}{" "}
          of this page, newest first.
        </p>
      )}
      <WikiResourceStatus
        refreshable={refreshable}
        state={history}
        label="history"
        onRefresh={() => onRefresh("history")}
      />
      {history.status === "ready" && revisions.length === 0 && (
        <p {...stylex.props(styles.bodyText)}>
          No revisions recorded for this page yet.
        </p>
      )}
      <ol {...stylex.props(localStyles.revisions)}>
        {revisions.map((revision, i) => (
          <WikiRevisionRow
            key={revision.id}
            revision={revision}
            older={revisions[i + 1]}
            isCurrent={revision.id === currentRevisionId}
            canEdit={canEdit}
            revertingId={revertingId}
            onDiff={onDiff}
            onRevert={onRevert}
            renderLink={renderLink}
          />
        ))}
      </ol>
      {diff && <WikiRevisionDiff older={diff.older} newer={diff.newer} />}
    </section>
  );
}

const localStyles = stylex.create({
  revisions: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: colors.border,
  },
});
